import { format } from 'svelte-i18n';
import { get } from 'svelte/store';

import { checkDeviceForceUpdate } from 'src/helpers/firmware_helpers';
import type { Device } from 'src/types/Device';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
import type { VideoContainerState } from '../types/video-container';
import type { CameraStatus, CameraStreamer } from '../types/camera-status';
import { CAMERA_ERROR } from '../types/camera-errors';
import type { WebRTCStreamError } from '../types/webrtc-session';
import { getCameraComponentKey } from './camera-helpers';
import { MAX_CAMERA_STREAMS } from './webrtc-config';

const _format = get(format);

/**
 * True when the camera cannot be used until firmware is updated:
 * mandatory device FW (`checkDeviceForceUpdate`) or streamer filesystem
 * incompatibility (`streamer_fs_bad`).
 */
export function isCameraFirmwareUpdateRequired(
	device?: Device | null,
	status?: DeviceStatusG2,
): boolean {
	if (!device) return false;
	if (checkDeviceForceUpdate(device)) return true;
	const cameraKey = getCameraComponentKey(device);
	const errors = (status?.[cameraKey] as CameraStatus | undefined)?.errors;
	return Array.isArray(errors) && errors.includes(CAMERA_ERROR.STREAMER_FS_BAD);
}

/**
 * Compute the camera's blocking state. Returns a VideoContainerState when the
 * stream cannot be played, or null when the camera is ready to stream.
 *
 * Precedence (highest first):
 *   1. offline → device is unreachable
 *   2. firmware → the camera needs an update before it can stream
 *   3. privacy → stream is disabled by user
 *   4. streams full → every slot is taken by someone else
 *   5. streamer !== 'running' → pipeline is transitioning (starting /
 *      stopping / stopped); WebRTC cannot connect yet
 *   6. support access → the camera is ready but this session may not watch it
 *
 * When this returns non-null, consumers should NOT mount the WebRTC player.
 *
 * @param privacyMode - Whether privacy mode is enabled
 * @param online - Whether the device is online
 * @param streamer - The `status['camera:0'].streamer` value from the device
 * @param firmwareUpdateRequired - From isCameraFirmwareUpdateRequired()
 * @param streams - The `status['camera:0'].streams` count from the device
 * @param hasSession - Whether this app already holds a session on the camera,
 * from `hasActiveWebRTCSession`. Our own stream is counted in `streams`, so
 * without this the view would block on the stream it is showing.
 * @param isSupportAccess - Whether the current JWT is a Support Access token.
 * Lowest precedence: the session sees what is wrong with the camera first.
 */
export function computeCameraState(
	privacyMode: boolean,
	online: boolean,
	streamer?: CameraStreamer | string,
	firmwareUpdateRequired = false,
	streams?: number,
	hasSession = false,
	isSupportAccess = false,
): VideoContainerState {
	if (!online) {
		return {
			type: 'offline',
			icon: 'fal fa-cloud-slash',
			heading: _format('device_status_offline', { default: 'Device Offline' }),
		};
	}
	if (firmwareUpdateRequired) {
		return {
			type: 'firmware',
			icon: 'fal fa-cloud-arrow-up',
			heading: _format('alert_heading_update_required', {
				default: 'Firmware update required',
			}),
		};
	}
	if (privacyMode) {
		return {
			type: 'privacy',
			icon: 'fas fa-video-slash',
			heading: _format('camera_privacy_mode', { default: 'Privacy Mode' }),
			message: _format('camera_privacy_mode_message', {
				default: 'Camera stream is disabled',
			}),
		};
	}

	// Joining would only earn a `-108` from the camera, so the view says why
	// instead of letting the player negotiate into a refusal it retries.
	if (!hasSession && (streams ?? 0) >= MAX_CAMERA_STREAMS) {
		return {
			type: 'error',
			icon: 'fal fa-video-slash',
			heading: _format('camera_max_streams_reached', {
				default: 'Reached {max} maximum streams',
				values: { max: String(MAX_CAMERA_STREAMS) },
			}),
		};
	}

	switch (streamer) {
		case 'starting':
			return {
				type: 'starting',
				heading: _format('camera_starting', { default: 'Starting camera' }),
				message: _format('camera_starting_message', {
					default: 'The camera stream is getting ready',
				}),
			};
		case 'stopping':
			return {
				type: 'starting',
				heading: _format('camera_stopping', { default: 'Stopping camera' }),
				message: _format('camera_stopping_message', {
					default: 'The camera stream is shutting down',
				}),
			};
		case 'stopped':
			return {
				type: 'starting',
				heading: _format('camera_stopped', { default: 'Camera stopped' }),
				message: _format('camera_stopped_message', {
					default: 'Waiting for the camera to start',
				}),
			};
		default:
			break;
	}

	// Nothing about the camera itself blocks the stream, so the viewer is the
	// only thing left. This comes last on purpose: a Support Access session is
	// there to diagnose the camera, so it reads the real state — offline,
	// firmware, privacy, full, starting — and only hears about its own
	// restriction once the camera would otherwise be streaming.
	if (isSupportAccess) {
		return {
			type: 'support_access',
			icon: 'fas fa-video-slash',
			heading: _format('camera_support_access', { default: 'Support Access' }),
			message: _format('camera_support_access_message', {
				default: 'Live Stream is restricted under Support Access',
			}),
		};
	}

	return null;
}

/**
 * Compute the stream state from the shared session and local video playback.
 * Returns a VideoContainerState for loading/error, or null if streaming normally.
 *
 * A loading session outranks its own error, because `failSession` sets both
 * before scheduling a reconnect: reporting that error would flash the error
 * state on every retry and unmount the player. An error left standing once
 * loading has finished is terminal and is reported with the camera's own
 * heading and message — see the `camera_error_received` gotcha in ERRORS.md for
 * how RPC text reads once it reaches the user.
 *
 * IMPORTANT: Pass all values as arguments (not from closure) so Svelte's compiler
 * can track them as reactive dependencies in $: statements.
 *
 * @param session - Shared connection state
 * @param playback - Local video element state
 */
export function computeStreamState(
	session: { loading: boolean; error: WebRTCStreamError },
	playback: { playing: boolean; failed: boolean },
): VideoContainerState {
	if (session.loading) {
		return { type: 'loading' };
	}
	if (playback.failed) {
		return {
			type: 'error',
			heading: _format('camera_video_play_error', { default: 'Video Play Error' }),
			message: _format('camera_video_play_error_message', {
				default: 'Unable to play the video stream.',
			}),
		};
	}
	if (session.error.heading) {
		return {
			type: 'error',
			heading: session.error.heading,
			message: session.error.message,
		};
	}
	// Media is flowing but this `<video>` has not started yet.
	if (!playback.playing) {
		return { type: 'loading' };
	}
	return null;
}

/**
 * Offline / firmware / error / privacy / support access — show a non-live
 * placeholder, not a video frame.
 */
export function isCameraPlaceholderState(state: VideoContainerState): boolean {
	return (
		state?.type === 'offline' ||
		state?.type === 'firmware' ||
		state?.type === 'error' ||
		state?.type === 'privacy' ||
		state?.type === 'support_access'
	);
}
